import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";

interface GrowthSquadMember {
  id: string;
  name: string;
  alias: string;
  email: string;
  user_id: string;
  type?: string;
  role?: string;
  description?: string | null;
}

interface UseGrowthSquadMembersOptions {
  typeFilter?: string;
}

export const useGrowthSquadMembers2Y3X = (options?: UseGrowthSquadMembersOptions) => {
  const [members, setMembers] = useState<GrowthSquadMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();

  const fetchMembers = async () => {
    if (!currentBrandId) {
      setMembers([]);
      return;
    }

    setIsLoading(true);
    try {
      let query = supabase
        .from("growth_squad")
        .select("id, name, alias, email, user_id, type, description")
        .eq("brand_id", currentBrandId);

      // Apply type filter if provided
      if (options?.typeFilter) {
        query = query.eq("type", options.typeFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch roles from user_brand_access for each member
      const membersWithRoles = await Promise.all(
        (data || []).map(async (member) => {
          // Get role from user_brand_access
          const { data: accessData } = await supabase
            .from('user_brand_access')
            .select('role')
            .eq('user_id', member.user_id)
            .eq('brand_id', currentBrandId)
            .maybeSingle();

          // If no access record, check if user owns the brand
          let role = accessData?.role;
          if (!role) {
            const { data: brandData } = await supabase
              .from('brands')
              .select('user_id')
              .eq('id', currentBrandId)
              .eq('user_id', member.user_id)
              .maybeSingle();
            
            if (brandData) {
              role = 'owner';
            }
          }

          return {
            ...member,
            role: role || undefined,
          };
        })
      );

      setMembers(membersWithRoles);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch growth squad members",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [currentBrandId, options?.typeFilter]);

  return {
    members,
    isLoading,
    refetch: fetchMembers,
  };
};
